"use client";

import Link from "next/link";
import {
  Check,
  Mail,
  Smartphone,
  AtSign,
  Store,
  ImageIcon,
  MessageSquareText,
} from "lucide-react";
import { useEffect, useMemo, type ReactNode } from "react";
import { IPhoneMockup } from "@/components/IPhoneMockup";
import { useLanguage } from "@/lib/useLanguage";
import { getTranslations } from "@/lib/translations";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { PublicFooterLanguage } from "@/components/PublicFooterLanguage";

const SUPPORT_EMAIL = "soporte@menuhunter.app";

function syncSupportPageHead(title: string, description: string) {
  if (typeof document === "undefined") return;
  document.title = title;

  const updateMetaTag = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", property);
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
  };

  const updateNameMetaTag = (name: string, content: string) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", name);
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
  };

  updateNameMetaTag("description", description);
  updateMetaTag("og:title", title);
  updateMetaTag("og:description", description);
  updateNameMetaTag("twitter:title", title);
  updateNameMetaTag("twitter:description", description);
}

function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {children}
    </p>
  );
}

export function SupportPageContent() {
  const { language } = useLanguage();
  const t = useMemo(() => getTranslations(language), [language]);
  const p = t.supportPage;

  useEffect(() => {
    syncSupportPageHead(p.documentTitle, p.metaDescription);
  }, [language, p.documentTitle, p.metaDescription]);

  const appSteps = [p.appStep1, p.appStep2, p.appStep3];

  const issueTypes = [
    p.issueAccount,
    p.issueSubscription,
    p.issueClaiming,
    p.issueMenu,
    p.issueTechnical,
    p.issueOther,
  ];

  const includeItems = [
    { icon: AtSign, text: p.includeEmail },
    { icon: Store, text: p.includeRestaurant },
    { icon: ImageIcon, text: p.includeScreenshots },
    { icon: MessageSquareText, text: p.includeDescription },
  ];

  return (
    <div
      key={language}
      className="min-h-screen flex flex-col bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100"
    >
      <MarketingHeader locale={language} />

      <main className="flex-1 flex flex-col" aria-live="polite">
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-10 sm:pt-12 pb-12 sm:pb-16">
          <header className="mb-10 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white leading-tight mb-3">
              {p.title}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              {p.intro}
            </p>
          </header>

          <div className="space-y-6 sm:space-y-8">
            {/* Email CTA */}
            <section
              className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white dark:border-slate-700/60 dark:from-slate-900/60 dark:to-slate-950/80 p-5 sm:p-6 shadow-sm shadow-slate-900/5 dark:shadow-black/20"
              aria-labelledby="support-email-heading"
            >
              <SectionEyebrow>{p.emailEyebrow}</SectionEyebrow>
              <h2
                id="support-email-heading"
                className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-2"
              >
                {p.emailTitle}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                {p.emailBody}
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 dark:focus:ring-offset-slate-950"
              >
                <Mail className="w-4 h-4 shrink-0" aria-hidden />
                {p.emailCta}
              </a>
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-500">
                {SUPPORT_EMAIL}
              </p>
            </section>

            {/* In-app support */}
            <section
              className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700/60 dark:bg-slate-900/30 overflow-hidden shadow-sm shadow-slate-900/5 dark:shadow-black/20"
              aria-labelledby="support-app-heading"
            >
              <div className="p-5 sm:p-6 lg:p-7">
                <div className="flex items-start gap-2 mb-1">
                  <Smartphone
                    className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5"
                    aria-hidden
                  />
                  <SectionEyebrow>{p.appEyebrow}</SectionEyebrow>
                </div>
                <h2
                  id="support-app-heading"
                  className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-2"
                >
                  {p.appTitle}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                  {p.appIntro}
                </p>
                <ol className="space-y-3 mb-4">
                  {appSteps.map((text, i) => (
                    <li
                      key={`${language}-app-step-${i}`}
                      className="flex gap-3 text-sm text-slate-700 dark:text-slate-300 leading-snug"
                    >
                      <span
                        className="flex-shrink-0 w-7 h-7 rounded-lg bg-slate-100 border border-slate-200 dark:bg-slate-800/90 dark:border-slate-600/50 flex items-center justify-center text-[11px] font-semibold text-slate-900 dark:text-slate-100 tabular-nums"
                        aria-hidden
                      >
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{text}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed border-t border-slate-100 dark:border-slate-700/50 pt-4">
                  {p.appDetail}
                </p>
              </div>
              <div className="border-t border-slate-100 dark:border-slate-700/50 bg-slate-50/80 dark:bg-slate-900/50 px-5 py-6 sm:px-6 flex justify-center">
                <div className="w-full max-w-[220px] sm:max-w-[240px]">
                  <IPhoneMockup
                    key={language}
                    src={p.mockupImageSrc}
                    alt={p.mockupAlt}
                    className="w-full"
                    sizes="240px"
                  />
                </div>
              </div>
            </section>

            {/* Issue types */}
            <section aria-labelledby="support-issues-heading">
              <SectionEyebrow>{p.issuesEyebrow}</SectionEyebrow>
              <h2
                id="support-issues-heading"
                className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-4"
              >
                {p.issuesTitle}
              </h2>
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {issueTypes.map((label, i) => (
                  <li
                    key={`${language}-issue-${i}`}
                    className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700/45 dark:bg-slate-800/30 px-3.5 py-2.5 flex items-start gap-2.5"
                  >
                    <Check
                      className="w-4 h-4 text-emerald-600 dark:text-emerald-400/90 shrink-0 mt-0.5"
                      aria-hidden
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300 leading-snug">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {/* What to include */}
            <section
              className="rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800/80 dark:bg-slate-900/25 px-5 py-5 sm:px-6 sm:py-6"
              aria-labelledby="support-include-heading"
            >
              <SectionEyebrow>{p.includeEyebrow}</SectionEyebrow>
              <h2
                id="support-include-heading"
                className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-4"
              >
                {p.includeTitle}
              </h2>
              <ul className="space-y-3">
                {includeItems.map(({ icon: Icon, text }, i) => (
                  <li
                    key={`${language}-include-${i}`}
                    className="flex gap-3 text-sm text-slate-700 dark:text-slate-300 leading-snug"
                  >
                    <Icon
                      className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0 mt-0.5"
                      aria-hidden
                    />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Fallback + download */}
            <div className="space-y-4 pt-2">
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                {p.fallback}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500">
                {p.downloadHint}{" "}
                <Link
                  href="/download"
                  className="text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white font-medium underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950 rounded"
                >
                  {p.downloadLink}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50 py-5 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <PublicFooterLanguage />
          </div>
          <div className="text-center text-xs text-slate-500">
            <p>
              &copy; {new Date().getFullYear()} {t.footer.copyright}
            </p>
            <p className="mt-1">
              <Link
                href="/restaurants"
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
              >
                {p.footerRestaurantsLink}
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
